import { DeviceType } from '@services/constants'
import { Device } from '@services/device'
import { useAllSessions, useSessionTree } from '@services/session'
import { Button, Input, Select } from '@shared/innmaxUI/Form'
import Form from '@shared/innmaxUI/Form/Form'
import React from 'react'
import { useTranslation } from 'react-i18next'
import OptionsSelect from 'src/components/OptionsSelect'
import styled from 'styled-components'
import { useDashboardContext } from '../../Provider'
import MainSidebar from '../MainSidebar'

type FilterBarType = {
  visible: boolean
  isLoading?: boolean
  filterResult?: Device[]
  close: () => void
}

export default function FiltersSidebar({
  visible,
  isLoading,
  filterResult,
  close,
}: FilterBarType) {
  const { t } = useTranslation()

  const { getFullSessions } = useAllSessions()

  const { byId } = useSessionTree()

  const {
    setFilterVariables,
    isFilterActived,
    filterVariables,
    currentSession,
  } = useDashboardContext()

  const [form] = Form.useForm()

  const handleSubmit = () => {
    form.validateFields().then((values: any) => {
      values?.deviceType?.length === 0 && delete values.deviceType
      values?.status &&
        Object.keys(values?.status).length === 0 &&
        delete values.status

      let queryVariables = {
        ...values,
        ...(values?.status ? values.status : { status: undefined }),
        timestamp: new Date().getTime(),
        deep: true,
      }
      if (values.filterSessionId && byId[values.filterSessionId]) {
        const filterSession = {
          ...byId[values.filterSessionId],
          sessionPath: getFullSessions(values.filterSessionId),
        }
        queryVariables = {
          ...queryVariables,
          filterSession,
        }
      }
      setFilterVariables(queryVariables)
    })
  }

  const resetForm = () => {
    form.resetFields()
    close()
    setFilterVariables(undefined)
  }

  React.useEffect(() => {
    if (!filterVariables) {
      return
    }
    form &&
      form.setFieldValue('status', {
        ...(filterVariables.available === true && {
          available: true,
        }),
        ...(filterVariables.charging === true && {
          charging: true,
        }),
        ...(filterVariables.unavailable === true && {
          unavailable: true,
        }),
      })
  }, [filterVariables]) //eslint-disable-line

  React.useEffect(() => {
    form.setFieldValue('filterSessionId', currentSession?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession])

  return (
    <MainSidebar
      visible={visible}
      onClose={close}
      headerMoreAction={
        <StyledResetForm type="link" onClick={resetForm}>
          {t('dashboard:reset')}
        </StyledResetForm>
      }>
      <StyledDialog className="flex-1 relative">
        <Form
          form={form}
          className="p-16"
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleSubmit}>
          <Form.Item
            label={t(
              'dashboard:session (search only within the scope of the session)'
            )}
            name="filterSessionId"
            initialValue={currentSession?.id}
            className="mb-32">
            <OptionsSelect.SessionTreeSelect
              currentSessionId={currentSession?.id as number | undefined}
            />
          </Form.Item>
          <Form.Item
            label={t('dashboard:search device (optional)')}
            name="input"
            className="mb-32">
            <Input
              type="text"
              autoComplete="off"
              placeholder={t('device:search_keyword')}
              allowClear
            />
          </Form.Item>
          <Form.Item
            label={t('device:device status')}
            name="status"
            className="mb-32">
            <OptionsSelect.ChargingPointStatusSelect />
          </Form.Item>
          <Form.Item
            label={t('device:device type')}
            name="deviceType"
            initialValue={DeviceType.CONNECTOR}
            className="mb-32">
            <Select>
              <Select.Option value={DeviceType.CHARGINGPOINT}>
                充電樁
              </Select.Option>
              <Select.Option value={DeviceType.CONNECTOR}>充電槍</Select.Option>
            </Select>
          </Form.Item>
          <Button
            block
            type="primary"
            size="large"
            loading={isLoading}
            htmlType="submit">
            {t('dashboard:search')}
          </Button>
          {isFilterActived && (
            <div className="mt-15 font-medium text-info">
              {/* 符合結果：{filterResult.length} 筆 */}
            </div>
          )}
        </Form>
      </StyledDialog>
    </MainSidebar>
  )
}

const StyledDialog = styled.div`
  max-width: 240px;
  box-shadow: -6px 0 10px 0 rgb(0 0 0 / 10%);
`

const StyledResetForm = styled(Button)`
  font-size: 12px !important;

  span {
    text-decoration: underline;
  }
`
